import React, { useEffect, useState, useContext } from "react";
import { Container, Row, Col, Form, Button, Spinner, Card } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { paymentsApi } from "../Components/paymentsApi";
import { toast } from "react-toastify";
import { CurrencyContext } from "../Contexts/CurrencyContext";

const CURRENCY_OPTIONS = [
  { code: "LKR", symbol: "Rs.", label: "Sri Lankan Rupee (Rs.)" },
  { code: "USD", symbol: "$", label: "US Dollar ($)" },
  { code: "GBP", symbol: "£", label: "British Pound (£)" },
  { code: "EUR", symbol: "€", label: "Euro (€)" },
];

export default function FinanceSettingsPage() {
  const navigate = useNavigate();
  const { refreshCurrency } = useContext(CurrencyContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    default_grace_period_days: 7,
    max_payment_stages: 12,
    require_receipt_upload: true,
    reminder_days_before_due: 3,
    overdue_status_name: "Outstanding Fees",
    fees_completed_status_name: "Fees Completed",
    payment_trigger_status_name: "",
    currency_code: "LKR",
    currency_symbol: "Rs.",
  });

  const load = async () => {
    setLoading(true);
    try {
      const data = await paymentsApi.getSettings();
      if (data) {
        setForm({
          default_grace_period_days: data.default_grace_period_days ?? 7,
          max_payment_stages: data.max_payment_stages ?? 12,
          require_receipt_upload: data.require_receipt_upload !== false,
          reminder_days_before_due: data.reminder_days_before_due ?? 3,
          overdue_status_name: data.overdue_status_name ?? "Outstanding Fees",
          fees_completed_status_name: data.fees_completed_status_name ?? "Fees Completed",
          payment_trigger_status_name: data.payment_trigger_status_name ?? "",
          currency_code: data.currency_code ?? "LKR",
          currency_symbol: data.currency_symbol ?? "Rs.",
        });
      }
    } catch (e) {
      toast.error(e.message || "Failed to load settings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await paymentsApi.updateSettings({
        default_grace_period_days: Number(form.default_grace_period_days) || 0,
        max_payment_stages: Number(form.max_payment_stages) || 12,
        require_receipt_upload: Boolean(form.require_receipt_upload),
        reminder_days_before_due: Number(form.reminder_days_before_due) || 0,
        overdue_status_name: String(form.overdue_status_name || "").trim() || "Outstanding Fees",
        fees_completed_status_name: String(form.fees_completed_status_name || "").trim() || "Fees Completed",
        payment_trigger_status_name: String(form.payment_trigger_status_name || "").trim() || null,
        currency_code: String(form.currency_code || "LKR").trim(),
        currency_symbol: String(form.currency_symbol || "Rs.").trim(),
      });
      refreshCurrency();
      toast.success("Finance settings saved.");
    } catch (e) {
      toast.error(e.message || "Failed to save settings.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4">
        <div className="d-flex align-items-center gap-2 mb-3">
          <Button variant="link" className="p-0" onClick={() => navigate(-1)}>
            ← Back
          </Button>
        </div>
        <h2 className="mb-4">Finance Settings</h2>

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" />
          </div>
        ) : (
          <Card className="shadow-sm">
            <Card.Body>
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>Default Grace Period (days)</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    value={form.default_grace_period_days}
                    onChange={(e) => handleChange("default_grace_period_days", e.target.value)}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Max Payment Stages</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    value={form.max_payment_stages}
                    onChange={(e) => handleChange("max_payment_stages", e.target.value)}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    label="Require receipt upload for payments"
                    checked={form.require_receipt_upload}
                    onChange={(e) => handleChange("require_receipt_upload", e.target.checked)}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Reminder days before due date</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    value={form.reminder_days_before_due}
                    onChange={(e) => handleChange("reminder_days_before_due", e.target.value)}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Overdue status name (enquiry)</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.overdue_status_name}
                    onChange={(e) => handleChange("overdue_status_name", e.target.value)}
                    placeholder="Outstanding Fees"
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Fees completed status name (enquiry)</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.fees_completed_status_name}
                    onChange={(e) => handleChange("fees_completed_status_name", e.target.value)}
                    placeholder="Fees Completed"
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Payment trigger status (stage that prompts Full/Payment Plan)</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.payment_trigger_status_name}
                    onChange={(e) => handleChange("payment_trigger_status_name", e.target.value)}
                    placeholder="e.g. Fee Payment Due"
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Currency</Form.Label>
                  <Form.Select
                    value={form.currency_code}
                    onChange={(e) => {
                      const opt = CURRENCY_OPTIONS.find((o) => o.code === e.target.value);
                      setForm((prev) => ({
                        ...prev,
                        currency_code: opt?.code ?? prev.currency_code,
                        currency_symbol: opt?.symbol ?? prev.currency_symbol,
                      }));
                    }}
                  >
                    {CURRENCY_OPTIONS.map((o) => (
                      <option key={o.code} value={o.code}>{o.label}</option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">Display symbol used across the app (e.g. Rs. 1,234.56). Change in Settings → Finance.</Form.Text>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Currency symbol (custom)</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.currency_symbol}
                    onChange={(e) => handleChange("currency_symbol", e.target.value)}
                    placeholder="Rs. or $ or £"
                  />
                  <Form.Text className="text-muted">Override symbol above if needed (e.g. LKR, ₨).</Form.Text>
                </Form.Group>
                <Button type="submit" variant="primary" disabled={saving}>
                  {saving ? <Spinner animation="border" size="sm" /> : "Save"}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        )}
    </div>
  );
}
